// CSS
import './FullResultDoc.css';
// React hooks
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// Skybase hooks
import { useQuery } from '../../../../context/QueryContext';
import { useData } from '../../../../context/DataContext';
import { useAuth } from '../../../../context/AuthContext';
import { API_BASE_URL } from '../../../../config';
// External Hooks/components
import { useAuth as useClerkAuth } from '@clerk/react';
// Media assets
import FolderIcon from '../../../../assets/StandardIcons/FolderIcons/yellow-folder-icon.png';   // Folder icon



// =============
// FullResultDoc – the Component that renders a Document as a "search result" on /results.
// =============
function FullResultDoc({
    uuid,               // String   – the Postgres UUID of the Document search result
    onOpenCallback,     // ()       – callback function triggered when this Component is opened
    onCloseCallback,    // ()       – callback function triggered when this Component is closed
}){

    // destructure global context
    const { activeQuery } = useQuery();
    const { changeDirectoryNodeId } = useData();
    const { isLoading: isClerkLoading, getToken } = useClerkAuth();
    const { isSubscribed, checkIsSubscribed } = useAuth();

    // init hooks
    const navigate = useNavigate('');

    // state vars
    const [documentJson, setDocumentJson] = useState(null);
    const [sourceFolderJson, setSourceFolderJson] = useState(null);
    const [sourceEngineJson, setSourceEngineJson] = useState(null);
    const [isDocumentPreviewModalOpen, setIsDocumentPreviewModalOpen] = useState(false);


    // ============
    // fetch() JSON
    // ============
    useEffect(() => {
    
        // don't attempt fetch if Clerk still loading
        if (isClerkLoading) return;

        const loadDocument = async () => {
            try {
                // =========================
                // 1. Check subscription
                // =========================
                const subscribed = await checkIsSubscribed();

                // Exit condition:
                // Never fetch subscribed-route data for unsubscribed users.
                if (!subscribed) { return; }

                // =========================
                // 2. Get Clerk JWT
                // =========================
                const token = await getToken();

                // Exit condition:
                // Never make authenticated requests without a JWT.
                if (!token) { return; }

                // =========================
                // 3. GET /documents/{id}
                // =========================
                const response_object = await fetch(
                    `${API_BASE_URL}/api/v1/documents/${encodeURIComponent(uuid)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (!response_object.ok) {
                    throw new Error(
                        `SKYBASE (client): HTTP error ${response_object.status} encountered when fetching document ${uuid}`
                    );
                }

                const doc_json = await response_object.json();

                // =========================
                // 4. GET /folders/{id}
                // =========================
                const folder_response = await fetch(
                    `${API_BASE_URL}/api/v1/folders/${encodeURIComponent(doc_json.source_folder_id)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                if (!folder_response.ok) {
                    throw new Error(
                        `SKYBASE (client): HTTP error (${folder_response.status}) - Fetching source_folder (ID: ${doc_json.source_folder_id})`
                    );
                }

                const source_folder_json = await folder_response.json();

                // =========================
                // 5. GET /engines/{id}
                // =========================
                const source_engine_response = await fetch(
                    `${API_BASE_URL}/api/v1/engines/${encodeURIComponent(source_folder_json.source_engine)}`
                );

                if (!source_engine_response.ok) {
                    throw new Error(
                        `SKYBASE (client): HTTP error (${source_engine_response.status}) - Fetching Engine (ID: ${source_folder_json.source_engine})`
                    );
                }

                const source_engine_json = await source_engine_response.json();

                // =========================
                // 6. Slight delay
                // =========================
                await new Promise(resolve => setTimeout(resolve, 500));

                // =========================
                // 7. Trigger re-renders
                // =========================
                setDocumentJson(doc_json);
                setSourceFolderJson(source_folder_json);
                setSourceEngineJson(source_engine_json);

            } catch (err) {
                console.error(err);
            }
        };

        loadDocument();

    }, [uuid, isClerkLoading]);

    
    // =======================
    // Handle onClick behavior
    // =======================
    const handleClick = (e) => {

        //get the selected text (if it exists)
        const selection = window.getSelection()?.toString();

        // Ignore click when text is selected (it exists)
        if (selection) {return;}

        // Otherwise, execute real click logic
        if (isDocumentPreviewModalOpen) {
            
            // close DocumentPreviewModal
            setIsDocumentPreviewModalOpen(false);

            // trigger callback
            onCloseCallback?.();
            
        } else {
            
            // open Document in DocumentPreviewModal
            setIsDocumentPreviewModalOpen(true);

            // trigger callback
            onOpenCallback?.();
        }

        
    };


    // ==========================================================
    // Behavior: Highlight text that exactly-matches search query (vibe-coded)
    // ==========================================================
    function highlightText(text, pattern) {
        
        // Exit: Pattern DNE
        if (!pattern) return text;

        // Escape special regex characters
        const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

        const regex = new RegExp(`(${escapedPattern})`, "gi");

        const parts = text.split(regex);

        return text.split(regex).map((part, index) => {
            if (part.toLowerCase() === pattern.toLowerCase()) {
                return <span key={index} style={{ backgroundColor: "yellow", color: "black", padding: "0px 2px"}}>{part}</span>
            }
            return part;
        });
    }


    // =============================
    // Handle SourceFolder onClick()
    // =============================
    const handleSourceFolderClick = () => {
        
        // set dir node ID
        changeDirectoryNodeId(sourceFolderJson.id);
        
        // redirect --> /directory
        navigate('../directory');
    }

    

    // JSX Component (Animated Placeholde) – rendered when JSON is still being fetched
    if (!documentJson) { return <div className='FullResultDoc FullResultDoc-animated-placeholder'></div> }

    
    
    // ===================
    // Create Preview Text
    // ⚠️ NOTE: This code must exist AFTER the Animated Placehold JSX to prevent fetch() race-condiiton errors (i.e. "cannot access JSON.body_text if it's still being fetched")
    // ===================
    // strip HTML so preview text fits comfortable within 4 lines
    const preview_text = documentJson?.body_html
        .replace(/<[^>]+>/g, "")
        .trim()
        .slice(0, 500);

    // ⚠️⚠️⚠️ TO-DO:
    // PROBLEM: The preview_text is VERY compute-expensive (creates tons of re-sizing lag when 100+ FullResultDoc's are being rendered.
    // SOLUTION: Process the preview_text in the BE, and send it to the FE to avoid truncation-related lag in the browser.



    // JSX Compoonent
    return(
        <>
            {/* === FullResultDoc (works for Desktop, Tablet, & Mobile) === */}
            <div className='FullResultDoc-mobile' onClick={handleClick}>

                {/* Top row */}
                <div className='FullResultDoc-mobile-top-row'>

                    {/* Title */}
                    <div className='FullResultDoc-mobile-title'>{highlightText(documentJson?.name, activeQuery)}</div>

                </div>
                

                {/* Metadata */}
                <div className='FullResultDoc-mobile-metadata-row'>

                    {/* Source folder */}
                    <div className="FullResultDoc-source-folder-chip" onClick={() => handleSourceFolderClick()}>
                        <img src={FolderIcon} className='FullResultDoc-folder-icon'/> {sourceFolderJson.name}
                    </div>

                    {/* Source URL */}
                    <div className='FullResultDoc-mobile-source-url'>
                        <a className='FullResultDoc-source-url-chip' href={documentJson.redirect_url} target='_blank' rel="noopener noreferrer">Source URL ⧉</a>
                    </div>

                </div>

                {/* Bottom row */}
                <div className='FullResultDoc-mobile-bottom-row'>

                    {/* Preview text – WARNING: The 'preview_text' creates massive LAG. */}
                    <div className='FullResultDoc-mobile-preview-text'>{preview_text}</div>

                </div>

            </div>
            
        </>
    );
};


// Export
export default FullResultDoc;